/**
 * Translates backend error messages to the user's selected language
 * @param errorMessage - The error message from the backend
 * @param t - The translation function from react-i18next
 * @returns The translated error message or the original message if no translation pattern matches
 */
export function translateBackendError(
	errorMessage: string,
	t: (key: string, params?: Record<string, string>) => string,
): string {
	// Pattern for staff schedule conflicts
	// Example: "Staff member has conflicting schedules: Schedule "Novo horario" conflicts with the new schedule. Time: 09:00-10:00"
	const staffConflictPattern =
		/^Staff member has conflicting schedules: Schedule "([^"]+)" conflicts with the new schedule\. Time: (.+)$/;

	const staffConflictMatch = errorMessage.match(staffConflictPattern);
	if (staffConflictMatch) {
		const [, scheduleName, timeRange] = staffConflictMatch;
		return t("schedules.errors.staffConflict", {
			scheduleName,
			timeRange,
		});
	}

	// Pattern for schedule-enrollment validation errors
	const inlineModeNotAllowedPattern =
		/^Inline mode not allowed for this organization$/;
	if (inlineModeNotAllowedPattern.test(errorMessage)) {
		return t("enrollments.errors.inlineModeNotAllowed");
	}

	const scheduleConflictsFoundPattern = /^Schedule conflicts found: (.+)$/;
	const scheduleConflictsMatch = errorMessage.match(
		scheduleConflictsFoundPattern,
	);
	if (scheduleConflictsMatch) {
		const [, details] = scheduleConflictsMatch;
		return t("enrollments.errors.scheduleConflictsFound", { details });
	}

	const invalidPayloadKindPattern = /^Invalid payload kind: (.+)$/;
	const invalidPayloadKindMatch = errorMessage.match(invalidPayloadKindPattern);
	if (invalidPayloadKindMatch) {
		const [, kind] = invalidPayloadKindMatch;
		return t("enrollments.errors.invalidPayloadKind", { kind });
	}

	const permissionDeniedPattern = /^You don't have permission to (.+)$/;
	const permissionDeniedMatch = errorMessage.match(permissionDeniedPattern);
	if (permissionDeniedMatch) {
		const [, action] = permissionDeniedMatch;
		return t("enrollments.errors.permissionDenied", { action });
	}

	const duplicateEnrollmentPattern =
		/^Customer is already enrolled in schedule "([^"]+)"$/;
	const duplicateEnrollmentMatch = errorMessage.match(
		duplicateEnrollmentPattern,
	);
	if (duplicateEnrollmentMatch) {
		const [, scheduleName] = duplicateEnrollmentMatch;
		return t("enrollments.errors.duplicateEnrollment", { scheduleName });
	}

	const weeklyLimitExceededPattern =
		/^Weekly enrollment limit exceeded: (\d+) enrollments per week maximum$/;
	const weeklyLimitExceededMatch = errorMessage.match(
		weeklyLimitExceededPattern,
	);
	if (weeklyLimitExceededMatch) {
		const [, limit] = weeklyLimitExceededMatch;
		return t("enrollments.errors.weeklyLimitExceeded", { limit });
	}

	const scheduleInactivePattern = /^Schedule "([^"]+)" is inactive$/;
	const scheduleInactiveMatch = errorMessage.match(scheduleInactivePattern);
	if (scheduleInactiveMatch) {
		const [, scheduleName] = scheduleInactiveMatch;
		return t("enrollments.errors.scheduleInactive", { scheduleName });
	}

	const capacityExceededPattern =
		/^Schedule "([^"]+)" has reached maximum capacity \((\d+)\)$/;
	const capacityExceededMatch = errorMessage.match(capacityExceededPattern);
	if (capacityExceededMatch) {
		const [, scheduleName, capacity] = capacityExceededMatch;
		return t("enrollments.errors.capacityExceeded", { scheduleName, capacity });
	}

	// Return original message if no pattern matches
	return errorMessage;
}
